import React, { useEffect, useState } from "react";
import { Button, Spinner, Table, Modal, Form, Alert, Badge, Tab, Tabs } from "react-bootstrap";
import {
  getAllRoleAssignments,
  assignRole,
  copyRolesFromUser,
  revokeRole,
  getRoleCatalog,
  getSolutionCatalog,
  getRoleSolutionAccessCatalog,
  upsertRoleSolutionAccess,
  RoleCatalogItem,
  RoleSolutionAccessItem,
  SolutionCatalogItem,
  UserRole,
  CopyUserRolesResult,
} from "@/api/adminApi";
import { FaUserPlus, FaTrash, FaCrown, FaUser, FaUsers, FaUserShield, FaTag, FaWpforms } from 'react-icons/fa';
import "./AdminRoleManagement.scss";

type RoleSolutionEditorRow = {
  solutionKey: string;
  solutionDisplayName: string;
  routePrefix?: string;
  canView: boolean;
  canAdmin: boolean;
  lastUpdateDt?: string;
};

const AdminRoleManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("assignments");
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [roleCatalog, setRoleCatalog] = useState<RoleCatalogItem[]>([]);
  const [solutionCatalog, setSolutionCatalog] = useState<SolutionCatalogItem[]>([]);
  const [accessCatalog, setAccessCatalog] = useState<RoleSolutionAccessItem[]>([]);
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>("");
  const [editorRows, setEditorRows] = useState<RoleSolutionEditorRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("User");
  const [copyPermissionsEnabled, setCopyPermissionsEnabled] = useState(false);
  const [copyFromUserEmail, setCopyFromUserEmail] = useState("");
  const [copyTargetUserEmail, setCopyTargetUserEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!selectedRoleCode || solutionCatalog.length === 0) {
      setEditorRows([]);
      return;
    }

    const roleKey = selectedRoleCode.toLowerCase();
    const accessMap = new Map<string, RoleSolutionAccessItem>();
    accessCatalog
      .filter((x) => x.roleCode.toLowerCase() === roleKey)
      .forEach((x) => accessMap.set(x.solutionKey.toLowerCase(), x));

    setEditorRows(
      solutionCatalog.map((solution) => {
        const existing = accessMap.get(solution.solutionKey.toLowerCase());
        return {
          solutionKey: solution.solutionKey,
          solutionDisplayName: solution.solutionDisplayName,
          routePrefix: solution.routePrefix,
          canView: existing?.canView ?? false,
          canAdmin: existing?.canAdmin ?? false,
          lastUpdateDt: existing?.lastUpdateDt,
        };
      })
    );
  }, [selectedRoleCode, solutionCatalog, accessCatalog]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assignments, rolesData, solutionsData, accessData] = await Promise.all([
        getAllRoleAssignments(),
        getRoleCatalog(),
        getSolutionCatalog(),
        getRoleSolutionAccessCatalog(),
      ]);

      setRoles(assignments);
      setRoleCatalog(rolesData);
      setSolutionCatalog(solutionsData);
      setAccessCatalog(accessData);

      const assignable = rolesData.filter((r) => r.isAssignable);
      if (assignable.length > 0) {
        setNewUserRole(assignable[0].roleCode);
      }

      if (rolesData.length > 0) {
        setSelectedRoleCode((current) => current || rolesData[0].roleCode);
      }
    } catch (err: any) {
      console.error("Failed to load role management data:", err);
      setError(err.response?.data?.message || "Failed to load role management data");
    } finally {
      setLoading(false);
    }
  };

  const loadAccessCatalog = async () => {
    const accessData = await getRoleSolutionAccessCatalog();
    setAccessCatalog(accessData);
  };

  const roleDisplayMap = roleCatalog.reduce((acc, role) => {
    acc[role.roleCode.toLowerCase()] = role.roleDisplayName;
    return acc;
  }, {} as Record<string, string>);

  const getRoleDisplayName = (roleCode: string) =>
    roleDisplayMap[roleCode.toLowerCase()] || (roleCode === "OutOfBandComms" ? "Out of Band Comms" : roleCode);

  const assignableRoles = roleCatalog.filter((r) => r.isAssignable);

  const handleAssignRole = async () => {
    if (!newUserEmail.trim() || !newUserRole) {
      setError("Email and role are required");
      return;
    }

    if (copyPermissionsEnabled && !copyFromUserEmail.trim()) {
      setError("Copy source user is required when copy permissions is enabled");
      return;
    }

    try {
      const targetUser = newUserEmail.trim();
      await assignRole(targetUser, newUserRole);

      let copyResult: CopyUserRolesResult | null = null;
      if (copyPermissionsEnabled && copyFromUserEmail.trim()) {
        copyResult = await copyRolesFromUser(copyFromUserEmail.trim(), targetUser);
      }

      const messageParts = [`Assigned ${newUserRole} role to ${targetUser}`];
      if (copyResult) {
        messageParts.push(
          `Copied ${copyResult.copiedRoles.length} role(s) from ${copyResult.sourceUserEmail}`
        );
        if (copyResult.skippedExistingRoles.length > 0) {
          messageParts.push(`Skipped ${copyResult.skippedExistingRoles.length} existing role(s)`);
        }
      }

      setSuccess(messageParts.join(". "));
      setShowAddModal(false);
      setNewUserEmail("");
      setCopyPermissionsEnabled(false);
      setCopyFromUserEmail("");
      if (assignableRoles.length > 0) {
        setNewUserRole(assignableRoles[0].roleCode);
      }
      await loadAll();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to assign/copy roles");
    }
  };

  const handleRevokeRole = async (roleAssignment: UserRole) => {
    if (!confirm(`Revoke ${roleAssignment.role} role from ${roleAssignment.userEmail}?`)) {
      return;
    }

    try {
      await revokeRole(roleAssignment.userRoleId);
      setSuccess(`Revoked ${roleAssignment.role} role from ${roleAssignment.userEmail}`);
      await loadAll();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to revoke role");
    }
  };

  const handleCopyPermissions = async () => {
    if (!copyFromUserEmail.trim() || !copyTargetUserEmail.trim()) {
      setError("Source and target users are required");
      return;
    }

    try {
      const result = await copyRolesFromUser(copyFromUserEmail.trim(), copyTargetUserEmail.trim());
      setSuccess(
        `Copied ${result.copiedRoles.length} role(s) from ${result.sourceUserEmail} to ${result.targetUserEmail}. ` +
          `Skipped ${result.skippedExistingRoles.length} existing and ${result.skippedInvalidRoles.length} non-assignable role(s).`
      );
      setShowCopyModal(false);
      setCopyFromUserEmail("");
      setCopyTargetUserEmail("");
      await loadAll();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to copy permissions");
    }
  };

  const setEditorValue = (solutionKey: string, canView: boolean, canAdmin: boolean) => {
    setEditorRows((rows) =>
      rows.map((row) =>
        row.solutionKey === solutionKey
          ? {
              ...row,
              canView,
              canAdmin,
            }
          : row
      )
    );
  };

  const handleSaveSolutionAccess = async () => {
    if (!selectedRoleCode) {
      return;
    }

    setSavingAccess(true);
    setError(null);

    try {
      const existingMap = new Map<string, RoleSolutionAccessItem>();
      accessCatalog
        .filter((x) => x.roleCode.toLowerCase() === selectedRoleCode.toLowerCase())
        .forEach((x) => existingMap.set(x.solutionKey.toLowerCase(), x));

      const updates = editorRows.filter((row) => {
        const existing = existingMap.get(row.solutionKey.toLowerCase());
        const prevView = existing?.canView ?? false;
        const prevAdmin = existing?.canAdmin ?? false;
        return prevView !== row.canView || prevAdmin !== row.canAdmin;
      });

      for (const row of updates) {
        await upsertRoleSolutionAccess({
          roleCode: selectedRoleCode,
          solutionKey: row.solutionKey,
          canView: row.canView,
          canAdmin: row.canAdmin,
          lastUpdateDt: row.lastUpdateDt,
        });
      }

      await loadAccessCatalog();
      setSuccess(
        updates.length > 0
          ? `Updated ${updates.length} access mapping(s) for ${getRoleDisplayName(selectedRoleCode)}.`
          : "No access changes to save."
      );
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to save solution access updates");
    } finally {
      setSavingAccess(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "SuperAdmin":     return <FaCrown className="text-warning" />;
      case "Admin":         return <FaUserShield className="text-danger" />;
      case "ServiceDesk":   return <FaUsers className="text-primary" />;
      case "EmployeeOffer": return <FaTag className="text-success" />;
      case "Forms":         return <FaWpforms className="text-info" />;
      case "Reviews":       return <FaUserShield className="text-dark" />;
      case "EmailApproval": return <FaUserShield className="text-secondary" />;
      case "Reports":       return <FaUserShield className="text-primary" />;
      case "Pim":           return <FaUserShield className="text-danger" />;
      case "PimAutomation": return <FaUserShield className="text-danger" />;
      case "GroupsAdmin":   return <FaUserShield className="text-dark" />;
      case "OutOfBandComms":return <FaUserShield className="text-warning" />;
      case "User":          return <FaUser className="text-secondary" />;
      default:              return <FaUser />;
    }
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, string> = {
      SuperAdmin:     "warning",
      Admin:          "danger",
      ServiceDesk:    "primary",
      EmployeeOffer:  "success",
      Forms:          "info",
      Reviews:        "dark",
      EmailApproval:  "secondary",
      Reports:        "primary",
      Pim:            "danger",
      PimAutomation:  "danger",
      GroupsAdmin:    "dark",
      OutOfBandComms: "warning",
      User:           "secondary"
    };
    return <Badge bg={variants[role] || "secondary"}>{getRoleDisplayName(role)}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Group roles by user
  const rolesByUser = roles.reduce((acc, role) => {
    if (!acc[role.userEmail]) {
      acc[role.userEmail] = [];
    }
    acc[role.userEmail].push(role);
    return acc;
  }, {} as Record<string, UserRole[]>);

  return (
    <div className="admin-role-management container-fluid p-4">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-0">Role Management</h4>
          <p className="text-muted">Manage user roles and solution permissions</p>
        </div>
        {activeTab === "assignments" && (
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => setShowCopyModal(true)}>
              Copy Permissions
            </Button>
            <Button variant="success" onClick={() => setShowAddModal(true)}>
              <FaUserPlus className="me-2" />
              Assign Role
            </Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "assignments")}>
          <Tab eventKey="assignments" title="Role Assignments">
            <div className="table-responsive mt-3">
              <Table hover>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Roles</th>
                    <th>Granted By</th>
                    <th>Granted Date</th>
                    <th style={{ width: "100px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(rolesByUser).map(([userEmail, userRoles]) => (
                    <React.Fragment key={userEmail}>
                      {userRoles.map((role, index) => (
                        <tr key={role.userRoleId}>
                          {index === 0 && (
                            <td rowSpan={userRoles.length}>
                              <div className="d-flex align-items-center gap-2">
                                <strong>{userEmail}</strong>
                              </div>
                            </td>
                          )}
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {getRoleIcon(role.role)}
                              {getRoleBadge(role.role)}
                            </div>
                          </td>
                          <td>{role.grantedBy}</td>
                          <td>{formatDate(role.grantedDt)}</td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRevokeRole(role)}
                              title="Revoke role"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {roles.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No role assignments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>

          <Tab eventKey="solutions" title="Solution Access">
            <div className="solution-access-panel mt-3">
              <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <Form.Group>
                  <Form.Label className="mb-1">Role</Form.Label>
                  <Form.Select
                    value={selectedRoleCode}
                    onChange={(e) => setSelectedRoleCode(e.target.value)}
                    style={{ minWidth: 280 }}
                  >
                    {roleCatalog.map((role) => (
                      <option key={role.roleCode} value={role.roleCode}>
                        {role.roleDisplayName} ({role.roleCode})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Button variant="primary" onClick={handleSaveSolutionAccess} disabled={savingAccess || !selectedRoleCode}>
                  {savingAccess ? "Saving..." : "Save Access Changes"}
                </Button>
              </div>

              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Solution</th>
                      <th>Route</th>
                      <th style={{ width: 120 }}>Can View</th>
                      <th style={{ width: 120 }}>Can Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editorRows.map((row) => (
                      <tr key={row.solutionKey}>
                        <td>
                          <div className="fw-semibold">{row.solutionDisplayName}</div>
                          <div className="small text-muted">{row.solutionKey}</div>
                        </td>
                        <td className="small text-muted">{row.routePrefix || "-"}</td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={row.canView}
                            onChange={(e) =>
                              setEditorValue(
                                row.solutionKey,
                                e.target.checked,
                                e.target.checked ? row.canAdmin : false
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={row.canAdmin}
                            disabled={!row.canView}
                            onChange={(e) =>
                              setEditorValue(
                                row.solutionKey,
                                e.target.checked ? true : row.canView,
                                e.target.checked
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                    {editorRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No solutions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Tab>
        </Tabs>
      )}

      {/* Add Role Modal */}
      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        setCopyPermissionsEnabled(false);
        setCopyFromUserEmail("");
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>User Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="username or username@enmax.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
              />
              <Form.Text className="text-muted">
                Enter username (e.g., jbarnes) or full email
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
              >
                {assignableRoles.map((role) => (
                  <option key={role.roleCode} value={role.roleCode}>{role.roleDisplayName}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                <div className="mt-2">
                  <strong>Catalog-backed role assignments:</strong>
                  <ul className="mb-0 mt-1">
                    <li><strong>Role list:</strong> loaded from SQL <code>roles</code> where assignable</li>
                    <li><strong>Access:</strong> managed in the Solution Access tab per role and solution</li>
                    <li><strong>SuperAdmin:</strong> retains full catalog access override</li>
                  </ul>
                </div>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2 role-copy-section">
              <Form.Check
                type="switch"
                id="copy-permissions-switch"
                label="Copy permissions from another user"
                checked={copyPermissionsEnabled}
                onChange={(e) => {
                  setCopyPermissionsEnabled(e.target.checked);
                  if (!e.target.checked) {
                    setCopyFromUserEmail("");
                  }
                }}
              />
              {copyPermissionsEnabled && (
                <>
                  <Form.Control
                    className="mt-2"
                    type="text"
                    placeholder="Source user (e.g., jsmith or jsmith@enmax.com)"
                    value={copyFromUserEmail}
                    onChange={(e) => setCopyFromUserEmail(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Copies active assignable roles from source user to the target user. Existing target roles are preserved.
                  </Form.Text>
                </>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleAssignRole}>
            Assign Role
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCopyModal} onHide={() => {
        setShowCopyModal(false);
        setCopyFromUserEmail("");
        setCopyTargetUserEmail("");
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Copy Permissions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Source User</Form.Label>
              <Form.Control
                type="text"
                placeholder="username or username@enmax.com"
                value={copyFromUserEmail}
                onChange={(e) => setCopyFromUserEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Target User</Form.Label>
              <Form.Control
                type="text"
                placeholder="username or username@enmax.com"
                value={copyTargetUserEmail}
                onChange={(e) => setCopyTargetUserEmail(e.target.value)}
              />
              <Form.Text className="text-muted">
                Merge behavior: existing target roles are kept. Only active assignable roles are copied.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCopyPermissions}>
            Copy Permissions
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminRoleManagement;
